import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Loader2, CheckCircle2, UserPlus, Mail, IdCard } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuscribitePage({ embed = false }) {
  const navigate = useNavigate();
  const [step, setStep] = useState("dni"); // dni | matriculado_sin_usuario | matriculado_con_usuario | no_matriculado | success
  const [loading, setLoading] = useState(false);
  const [dni, setDni] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [form, setForm] = useState({ email: "", nombre: "", apellido: "" });
  const [extraEmail, setExtraEmail] = useState("");
  const [extraSubmitting, setExtraSubmitting] = useState(false);

  const verificarDni = async (e) => {
    e?.preventDefault();
    if (!dni || dni.length < 6) return toast.error("Ingresá un DNI válido");
    try {
      setLoading(true);
      const r = await axios.post(`${API_URL}/public/suscripcion/verificar`, { documento: dni });
      setCheckResult(r.data);
      if (r.data.es_matriculado) {
        setStep(r.data.ya_tiene_usuario ? "matriculado_con_usuario" : "matriculado_sin_usuario");
      } else {
        setStep("no_matriculado");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error verificando DNI");
    } finally {
      setLoading(false);
    }
  };

  const enviarSuscripcion = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.nombre) return toast.error("Completá email y nombre");
    try {
      setLoading(true);
      const r = await axios.post(`${API_URL}/public/suscripcion/directa`, form);
      if (r.data.success) {
        toast.success(r.data.mensaje);
        setStep("success");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No pudimos completar la suscripción");
    } finally {
      setLoading(false);
    }
  };

  const suscribirEmailExtra = async (e) => {
    e?.preventDefault();
    if (!extraEmail || !extraEmail.includes("@")) {
      return toast.error("Ingresá un email válido");
    }
    // Reutilizamos el nombre que ya tenemos del matriculado
    const nombreMat = (checkResult?.nombre_ofuscado || "").split(" ")[0] || "Matriculadx";
    try {
      setExtraSubmitting(true);
      const r = await axios.post(`${API_URL}/public/suscripcion/directa`, {
        email: extraEmail,
        nombre: nombreMat,
        apellido: "",
      });
      if (r.data.success) {
        toast.success(r.data.mensaje);
        setExtraEmail("");
        setStep("success");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No pudimos suscribir ese correo");
    } finally {
      setExtraSubmitting(false);
    }
  };

  return (
    <div className={embed
      ? "bg-transparent p-4"
      : "min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center px-4 py-12"
    } data-testid="suscribite-page">
      <div className={embed ? "w-full max-w-xl mx-auto" : "w-full max-w-xl"}>
        {!embed && (
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Suscribite al boletín</h1>
            <p className="text-gray-600">
              Colegio de Trabajadorxs Sociales · Lomas de Zamora
            </p>
          </div>
        )}

        {/* STEP 1: DNI */}
        {step === "dni" && (
          <Card className="border-purple-200">
            <CardContent className="p-6">
              <form onSubmit={verificarDni} className="space-y-4">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <IdCard className="w-5 h-5 text-purple-600" />
                  <span className="text-sm">
                    Primero necesitamos saber si sos matriculadx en Lomas.
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    DNI (sin puntos, sin espacios)
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Ej: 30054218"
                    value={dni}
                    onChange={(e) => setDni(e.target.value.replace(/\D/g, ""))}
                    className="text-lg"
                    data-testid="input-dni"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || dni.length < 6}
                  className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-base"
                  data-testid="btn-verificar-dni"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continuar"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* STEP: matriculado sin usuario del portal */}
        {step === "matriculado_sin_usuario" && (
          <Card className="border-emerald-300 bg-emerald-50/40">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <UserPlus className="w-7 h-7 text-emerald-700" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                ¡Bienvenidx, {checkResult?.nombre_ofuscado}!
              </h2>
              <p className="text-gray-700 text-sm">
                Sos matriculadx del Colegio. Antes de suscribirte, te pedimos que
                <b> crees tu usuario en el portal</b>. Una vez que confirmes tu email,
                te vamos a suscribir automáticamente al boletín.
              </p>
              <Button
                onClick={() => navigate(`/register?trabajador_id=${checkResult?.trabajador_id}`)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-base"
                data-testid="btn-ir-a-registro"
              >
                Crear mi usuario del portal
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setStep("dni"); setDni(""); setCheckResult(null); }}
                className="text-gray-500"
              >
                Volver
              </Button>
            </CardContent>
          </Card>
        )}

        {/* STEP: matriculado con usuario ya creado */}
        {step === "matriculado_con_usuario" && (
          <Card className="border-blue-300 bg-blue-50/40">
            <CardContent className="p-6 space-y-4">
              <div className="text-center space-y-3">
                <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-blue-700" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Hola de nuevo, {checkResult?.nombre_ofuscado}
                </h2>
                <p className="text-gray-700 text-sm">
                  Ya tenés tu usuario en el portal. Si estás matriculadx al día,
                  probablemente <b>ya recibas nuestro boletín</b>.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate("/login")}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 py-6"
                    data-testid="btn-ir-a-login"
                  >
                    Ingresar al portal
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setStep("dni"); setDni(""); setCheckResult(null); }}
                    className="py-6"
                  >
                    Volver
                  </Button>
                </div>
              </div>

              {/* Suscribir email alternativo */}
              <div className="border-t border-blue-200 pt-4 mt-4">
                <p className="text-sm font-medium text-gray-800 mb-1">
                  ¿Querés recibir las novedades en otro correo?
                </p>
                <p className="text-xs text-gray-600 mb-3">
                  Ingresá un email adicional (personal, familiar, laboral) para recibir el boletín también ahí.
                </p>
                <form onSubmit={suscribirEmailExtra} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="otro-email@ejemplo.com"
                    value={extraEmail}
                    onChange={(e) => setExtraEmail(e.target.value)}
                    className="flex-1"
                    data-testid="input-email-extra"
                  />
                  <Button
                    type="submit"
                    disabled={extraSubmitting || !extraEmail}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="btn-suscribir-extra"
                  >
                    {extraSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suscribir"}
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP: no matriculado → formulario simple */}
        {step === "no_matriculado" && (
          <Card className="border-purple-200">
            <CardContent className="p-6">
              <form onSubmit={enviarSuscripcion} className="space-y-4">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <Mail className="w-5 h-5 text-purple-600" />
                  <span className="text-sm">
                    Completá tus datos para recibir el boletín.
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Nombre *</label>
                    <Input
                      value={form.nombre}
                      onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      data-testid="input-nombre"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Apellido</label>
                    <Input
                      value={form.apellido}
                      onChange={(e) => setForm({ ...form, apellido: e.target.value })}
                      data-testid="input-apellido"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="input-email"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-base"
                  data-testid="btn-enviar-suscripcion"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Suscribirme"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStep("dni"); setDni(""); }}
                  className="w-full text-gray-500"
                >
                  Volver
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* STEP: success */}
        {step === "success" && (
          <Card className="border-green-300 bg-green-50/40">
            <CardContent className="p-8 text-center space-y-3">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-700" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">¡Listo!</h2>
              <p className="text-gray-700 text-sm">
                Ya estás suscriptx a nuestro boletín. Te vamos a estar escribiendo pronto.
              </p>
              <Button
                variant="outline"
                onClick={() => window.location.href = "https://www.colegiotslomas.org"}
                className="mt-4"
              >
                Volver al sitio
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
